import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { isComsEnabled, isCustomChartEnabled, isCustomizeEmailFromAdminEnabled, isCustomRegionEnabled, isModularHomeExperienceEnabled, isOrgUiEnabled, } from '@thoughtspot/session-service';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BasePageContext } from "../../../contexts/basepage-context";
import { useGetSidebarConfigCacheFirst } from '../../../services/sidebar/sidebar-service';
import { CUSTOMISATION_TAB_IDS, getCustomisationItemTabs, } from '../../../utils/sidebar-config';
import { EmailCustomizations, HelpCustomization, } from '../../application-settings';
import { ChartCustomization } from '../../chart-customization';
import { HomePageDiscovery } from '../../home-page-discovery';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { CustomizationsTranslationKeys } from '../constants';
import { ALL_ORG_VIEW_MODE, resolveAdminOrgContext, } from '../user-management/shared/admin-org-context';
import styles from './customizations.module.scss';
const StyleTab = ({ orgViewModeType, }) => {
    const { styleComponents } = BasePageContext();
    const { StyleComponentsContainer } = styleComponents || {};
    if (!StyleComponentsContainer) {
        return (React.createElement(LoadingIndicator, null,
            React.createElement(React.Fragment, null)));
    }
    return (React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator, null,
            React.createElement(React.Fragment, null)) },
        React.createElement(StyleComponentsContainer, { orgViewModeType: orgViewModeType })));
};
export const CustomizationsContainer = ({ currentOrgId, orgViewModeType, initialTab = 'style', }) => {
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const [activeTab, setActiveTab] = useState(initialTab);
    const orgUiEnabled = isOrgUiEnabled();
    const viewMode = useMemo(() => {
        const { isAllOrgContext, isPrimaryOrgContext } = resolveAdminOrgContext({
            orgUiEnabled,
            orgViewModeType,
        });
        if (isAllOrgContext)
            return 'all';
        if (isPrimaryOrgContext)
            return 'primary';
        return '';
    }, [orgUiEnabled, orgViewModeType]);
    const { data: sidebarConfig, error: sidebarError, getSidebarConfig, } = useGetSidebarConfigCacheFirst();
    useEffect(() => {
        getSidebarConfig({ variables: { viewMode } });
    }, [viewMode, getSidebarConfig]);
    const sidebarSettled = sidebarConfig !== undefined || !!sidebarError;
    const allowedTabs = useMemo(() => getCustomisationItemTabs(sidebarConfig), [
        sidebarConfig,
    ]);
    const isTabAllowed = (id, legacyShow) => allowedTabs === null ? legacyShow : allowedTabs.includes(id);
    const tabs = useMemo(() => [
        {
            id: CUSTOMISATION_TAB_IDS.style,
            title: t(CustomizationsTranslationKeys.TAB_STYLE),
            show: isTabAllowed(CUSTOMISATION_TAB_IDS.style, true),
        },
        {
            id: CUSTOMISATION_TAB_IDS.chart,
            title: t(CustomizationsTranslationKeys.TAB_CHART),
            show: isTabAllowed(CUSTOMISATION_TAB_IDS.chart, isCustomChartEnabled() || isCustomRegionEnabled()),
        },
        {
            id: CUSTOMISATION_TAB_IDS.homepage,
            title: t(CustomizationsTranslationKeys.TAB_HOMEPAGE),
            show: isTabAllowed(CUSTOMISATION_TAB_IDS.homepage, isModularHomeExperienceEnabled()),
        },
        {
            id: CUSTOMISATION_TAB_IDS.email,
            title: t(CustomizationsTranslationKeys.TAB_EMAIL),
            show: isTabAllowed(CUSTOMISATION_TAB_IDS.email, isComsEnabled() && isCustomizeEmailFromAdminEnabled()),
        },
        {
            id: CUSTOMISATION_TAB_IDS.help,
            title: t(CustomizationsTranslationKeys.TAB_HELP),
            show: isTabAllowed(CUSTOMISATION_TAB_IDS.help, orgViewModeType === ALL_ORG_VIEW_MODE),
        },
    ], [t, orgViewModeType, allowedTabs]);
    const visibleTabs = useMemo(() => tabs.filter(tab => tab.show !== false), [
        tabs,
    ]);
    useEffect(() => {
        if (visibleTabs.length > 0 &&
            !visibleTabs.some(tab => tab.id === activeTab)) {
            setActiveTab(visibleTabs[0].id);
        }
    }, [activeTab, visibleTabs]);
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: `admin_customizations_${tabId}`,
        });
    };
    const renderTabContent = () => {
        switch (activeTab) {
            case CUSTOMISATION_TAB_IDS.style:
                return React.createElement(StyleTab, { orgViewModeType: orgViewModeType });
            case CUSTOMISATION_TAB_IDS.chart:
                return React.createElement(ChartCustomization, { currentOrgId: currentOrgId });
            case CUSTOMISATION_TAB_IDS.homepage:
                return React.createElement(HomePageDiscovery, { orgViewModeType: orgViewModeType });
            case CUSTOMISATION_TAB_IDS.email:
                return React.createElement(EmailCustomizations, { currentOrgId: currentOrgId });
            case CUSTOMISATION_TAB_IDS.help:
                return React.createElement(HelpCustomization, null);
            default:
                return null;
        }
    };
    return (React.createElement("div", { className: styles.customizationsContainer },
        React.createElement(AdminPageHeader, { title: t(CustomizationsTranslationKeys.TITLE), tabs: visibleTabs, selectedTabId: activeTab, onTabChange: handleTabChange, titleTestId: "customizations-title", tabTestIdPrefix: "customizations-tab" }),
        React.createElement("div", { className: styles.tabContent }, sidebarSettled ? renderTabContent() : null)));
};
//# sourceMappingURL=customizations.container.js.map